import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import addTreemapModule from "highcharts/modules/treemap";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Constant } from "app/constants/constant";
import { WorkGhgsService } from "app/services/work/WorkGhgsService";
import { ResponseResultCode } from "app/model/response-api";
import { WorkGhgsBzkndModel } from "app/model/work/workGhgsModel";
import { numberFormatter } from "app/utils/stringUtil";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import { useNavigate } from "react-router-dom";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";

const defaultOption: Highcharts.Options = {
  chart: {
    height: 210,
  },
  tooltip: {
    useHTML: true,
    formatter(this: any) {
      return `
        <div>
          <span style="color:${this.color};">●&nbsp;</span>
          <span style="font-weight:500;">${this.point.name}:&nbsp;</span>
          <span style="font-weight:700;">${numberFormatter(this.point.value)}</span> tCO₂eq
        </div>
      `;
    }
  },
  plotOptions: {
    treemap: {
      dataLabels: {
        useHTML: true,
        formatter(this: any) {
          return `
            <span style="color: #FFF;">
              ${this.point.name}
            </span>`;
        }
      }
    }
  },
  series: [{
    type: 'treemap',
    clip: false,
    data: []
  }]
};

export default function DbBzkndGhgs() {
  addTreemapModule(Highcharts);
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    title: { text: "" },
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const navigate = useNavigate();
  const colors: string[] = [...HighchartsOptionsUtil.eCloudColors];

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [option, setOption] = useState<Highcharts.Options>(defaultOption);

  useEffect(() => {
    fetch();
  }, []);

  useInterval(() => {
    fetch();
  }, 600000);

  function fetch() {
    const param = {
      searchType: 'MONTH',
      fromDt: dayjs().startOf('year').format(Constant.FORMAT_YMD_ALT),
      toDt: dayjs().endOf('year').format(Constant.FORMAT_YMD_ALT)
    };
    WorkGhgsService.getInstance().getEmissionsByBzknds(param)
    .then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        const responseData: WorkGhgsBzkndModel[] = response.data ?? [];
        const sortedData = responseData.sort((a, b) => ((a.total??0) - (b.total??0)) * -1);
        const datas = sortedData.map((item, index) => {
          return { name: item.bzkndNm, value: item.total, color: colors[index%colors.length] };
        });

        const height = document.getElementById('dbAdminBzkndEmsnChartDiv')?.clientHeight ?? 210;
        setOption({
          ...defaultOption,
          chart: {
            height: `${height * 0.77}`,
            marginBottom: 5
          },
          series: [{
            type: 'treemap',
            layoutStartingDirection: 'horizontal',
            clip: false,
            data: datas ?? [],
          }]
        });
      }
    })
    .catch(() => {
      setOption({ ...defaultOption });
    })
    .finally(() => {
      setIsLoading(false);
    })
  }

  return (
    <div className="cont" id="dbAdminBzkndEmsnChartDiv">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl"><span>업종별 온실가스 배출량</span></h2>
      <div className="chart">
        {option && (
          <HighchartsReact highcharts={Highcharts} options={option} />
        )}
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/work/ghgs/bzknd')}
      />
    </div>
  );
}